import * as Common from '@nimbly-technologies/nimbly-common';
import moment, { Moment } from 'moment-timezone';

import { currentPeriodStartDate, currentPeriodEndDate } from '../utils/schedule';

/* BASE FUNCTION */
// Returns array of string date from startDate to dueDate
export function getSelectedPeriodDates(analytics: any) {
	if (analytics.periodType === 'custom') {
		if (analytics.customStart && analytics.customEnd) {
			const periodDate = analytics.customStart.clone();
			const periodDueDate = analytics.customEnd;
			const chartPeriodDates = [];
			while (periodDate.isSameOrBefore(periodDueDate)) {
				chartPeriodDates.push(periodDate.format('YYYY-MM-DD'));
				periodDate.add(1, 'day');
			}
			return chartPeriodDates;
		}
	}
	const periodDate = analytics.startDate.clone();
	const periodDueDate = analytics.endDate;
	const chartPeriodDates = [];
	while (periodDate.isSameOrBefore(periodDueDate)) {
		chartPeriodDates.push(periodDate.format('YYYY-MM-DD'));
		periodDate.add(1, 'day');
	}
	return chartPeriodDates;
}

const currentPeriodYear = () => {
	return moment.parseZone().startOf('year');
};

const currentPeriodMonth = () => {
	return moment.parseZone().startOf('month');
};

const prevPeriodYear = () => {
	return moment.parseZone().subtract(1, 'year').startOf('year');
};

const prevPeriodMonth = () => {
	return moment.parseZone().subtract(1, 'month').startOf('month');
};

export const getDatesByPeriod = (
	periodType: Common.enums.PeriodTypes | 'ytd' | null,
	orgSchedule: Common.OrganizationSchedule,
	date: any = null,
	previous = false,
	// eslint-disable-next-line max-params
): { startDate: Moment | null; endDate: Moment | null } => {
	switch (periodType) {
		case Common.enums.PeriodTypes.CURRENT:
			return {
				startDate: currentPeriodStartDate(orgSchedule),
				endDate: currentPeriodEndDate(orgSchedule),
			};
		case Common.enums.PeriodTypes.PREVIOUS: {
			const periodUnit = orgSchedule.periodUnit as Common.PeriodUnit;
			return {
				startDate: currentPeriodStartDate(orgSchedule)?.subtract(1, periodUnit) || null,
				endDate: currentPeriodEndDate(orgSchedule)?.subtract(1, periodUnit) || null,
			};
		}
		case Common.enums.PeriodTypes.LAST_7:
			return {
				startDate: moment().subtract(6, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_28:
			return {
				startDate: moment().subtract(27, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_30:
			return {
				startDate: moment().subtract(29, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_90:
			return {
				startDate: moment().subtract(89, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_6_M:
			return {
				startDate: moment().subtract(30 * 6 - 1, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_12_M:
			return {
				startDate: moment().subtract(30 * 12 - 1, 'days'),
				endDate: moment(),
			};
		case Common.enums.PeriodTypes.LAST_YEAR:
			return {
				startDate: moment().subtract(1, 'year').startOf('year'),
				endDate: moment().subtract(1, 'year').endOf('year'),
			};
		case Common.enums.PeriodTypes.MTD:
			if (previous) {
				return {
					startDate: prevPeriodMonth(),
					endDate: moment.parseZone().subtract(1, 'month'),
				};
			}
			return {
				startDate: currentPeriodMonth(),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.CUSTOM:
			if (previous) {
				const customDate = moment(date.customStart);
				const diff = customDate.diff(date.customEnd, 'd');
				const prevCustomStartDate = moment(date.customStart).subtract(Math.abs(diff), 'd');
				const prevCustomEndDate = moment(date.customStart).subtract(1, 'ms');
				return {
					startDate: prevCustomStartDate,
					endDate: prevCustomEndDate,
				};
			}
			return {
				startDate: date.customStart,
				endDate: date.customEnd,
			};
		case 'ytd':
			if (previous) {
				return {
					startDate: prevPeriodYear(),
					endDate: moment.parseZone().subtract(1, 'year'),
				};
			}
			return {
				startDate: currentPeriodYear(),
				endDate: moment.parseZone(),
			};
		default:
			return {
				startDate: moment.parseZone().subtract(1, 'week'),
				endDate: moment.parseZone(),
			};
	}
};

export function getDatesByPeriodType(periodType: Common.enums.PeriodTypes) {
	switch (periodType) {
		case Common.enums.PeriodTypes.CURRENT:
			return {
				startDate: moment.parseZone().startOf('isoWeek'),
				endDate: moment.parseZone().endOf('isoWeek'),
			};
		case Common.enums.PeriodTypes.PREVIOUS:
			return {
				startDate: moment.parseZone().startOf('isoWeek').subtract(1, 'week'),
				endDate: moment.parseZone().endOf('isoWeek').subtract(1, 'week'),
			};
		case Common.enums.PeriodTypes.LAST_7:
			return {
				startDate: moment.parseZone().subtract(6, 'days'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_28:
			return {
				startDate: moment.parseZone().subtract(27, 'days'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_30:
			return {
				startDate: moment.parseZone().subtract(29, 'days'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_90:
			return {
				startDate: moment.parseZone().subtract(89, 'days'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_6_M:
			return {
				startDate: moment.parseZone().subtract(6, 'months').add(1, 'day'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_12_M:
			return {
				startDate: moment.parseZone().subtract(12, 'months').add(1, 'day'),
				endDate: moment.parseZone(),
			};
		case Common.enums.PeriodTypes.LAST_YEAR:
			return {
				startDate: moment.parseZone().subtract(1, 'year').startOf('year'),
				endDate: moment.parseZone().subtract(1, 'year').endOf('year'),
			};
		case Common.enums.PeriodTypes.MTD:
			return {
				startDate: moment.parseZone().startOf('month'),
				endDate: moment.parseZone(),
			};
		default:
			return {
				startDate: moment.parseZone().startOf('isoWeek'),
				endDate: moment.parseZone().endOf('isoWeek'),
			};
	}
}

const DATE_FORMAT_YYYY_MM_DD = 'YYYY-MM-DD';

/**
 * Safely formats a date value to YYYY-MM-DD string format.
 * Handles null, undefined, invalid dates, and both Moment objects and date strings.
 *
 * @param date - The date to format (Moment object, string, Date, null, or undefined)
 * @param fallback - Optional fallback value if date is invalid (defaults to undefined)
 * @returns Formatted date string in YYYY-MM-DD format, or fallback if invalid
 *
 * @example
 * // With Moment object
 * formatDateSafe(moment('2024-01-15')) // '2024-01-15'
 *
 * // With null - returns fallback
 * formatDateSafe(null) // undefined
 * formatDateSafe(null, '') // ''
 *
 * // With invalid date string
 * formatDateSafe('Invalid date') // undefined
 *
 * // UNSAFE pattern this replaces:
 * // moment(filters.startDate)?.format('YYYY-MM-DD') || '' // Returns 'Invalid date' when null!
 */
export function formatDateSafe(
	date: Moment | string | Date | null | undefined,
	fallback?: string,
): string | undefined {
	// Handle null/undefined/empty string
	if (date === null || date === undefined || date === '') {
		return fallback;
	}

	// If it's already a Moment object, check validity and format
	if (moment.isMoment(date)) {
		return date.isValid() ? date.format(DATE_FORMAT_YYYY_MM_DD) : fallback;
	}

	// Handle string 'Invalid date' explicitly
	if (typeof date === 'string' && date === 'Invalid date') {
		return fallback;
	}

	// For strings, try strict YYYY-MM-DD parsing first
	if (typeof date === 'string') {
		const strictParsed = moment(date, DATE_FORMAT_YYYY_MM_DD, true);
		if (strictParsed.isValid()) {
			return strictParsed.format(DATE_FORMAT_YYYY_MM_DD);
		}
	}

	// Try lenient parsing for Date objects and other string formats
	const lenientParsed = moment(date);
	if (lenientParsed.isValid()) {
		return lenientParsed.format(DATE_FORMAT_YYYY_MM_DD);
	}

	return fallback;
}

/**
 * Checks if a date string is in valid YYYY-MM-DD format.
 *
 * @param dateStr - The date string to validate
 * @returns true if valid YYYY-MM-DD format, false otherwise
 */
export function isValidDateString(dateStr: string | null | undefined): boolean {
	if (!dateStr) {
		return false;
	}
	return moment(dateStr, DATE_FORMAT_YYYY_MM_DD, true).isValid();
}
